import React, { useEffect, useRef, useState } from 'react';
import { LogOut, Calendar, Newspaper, ChevronDown, User, FileText } from 'lucide-react';
import { fetchBeritaData, fetchLaporData, fetchPersonalData } from '../../controller/mobile/home';

// ======================== TYPES ========================

interface BeritaItemData {
  title: string;
  slug: string;
  content: string;
  author: string;
  published_date: string;
  read_time_minutes: string;
  covert_image_url: string;
  views: number;
  tags: string;
  authorized: string;
  publish: string;
}

interface LaporItemData {
  no_pelanggan: string;
  nama: string;
  gambar: string;
  gambar_eksekusi: string;
  no_telp: string;
  deskripsi: string;
  deskripsi_eksekusi: string;
  lokasi: string;
  alamat: string;
  tanggal: string | null;
  status: string;
  rating: string;
  responden: string;
  eksekutor: string;
  laporan_path: string | null;
  berita_acara_path: string | null;
  authorized: string;
  modified_at: string;
}

interface DashboardCardItem {
  label: string;
  icon: 'person' | 'document' | 'camera';
  bgColor: string;
  route: string;
}

interface PersonalData {
  nama: string;
  nik: string;
  foto_karyawan?: string;
  contract_end_date?: string;
}

// ======================== HELPERS ========================

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'belum respon': return '#ef4444';
    case 'respon':       return '#3b82f6';
    case 'eksekusi':     return '#f59e0b';
    case 'selesai':      return '#22c55e';
    default:             return '#94a3b8';
  }
};

const getMenuIcon = (icon: string, size: number, color: string): React.ReactNode => {
  switch (icon) {
    case 'person':   return <User size={size} color={color} />;
    case 'document': return <FileText size={size} color={color} />;
    default:         return <User size={size} color={color} />;
  }
};

// ======================== STYLES ========================

const s: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f1f5f9',
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
  },
  content: {
    maxWidth: 480,
    margin: '0 auto',
    padding: '32px 16px 80px',
  },
  // --- Header ---
  headerUser: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#1e3a5f',
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    backgroundColor: '#3b82f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  avatarText: {
    color: 'white',
    fontWeight: 700,
    fontSize: 16,
  },
  headerTitleUser: {
    display: 'block',
    color: 'white',
    fontWeight: 700,
    fontSize: 13,
    letterSpacing: 0.3,
  },
  pointsText: {
    display: 'block',
    color: '#94a3b8',
    fontSize: 11,
    marginTop: 2,
  },
  iconButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 8,
    display: 'flex',
    alignItems: 'center',
  },
  // --- Sub Header ---
  subHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: 12,
    padding: '12px 16px',
    marginBottom: 12,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    gap: 8,
  },
  subHeaderText: {
    color: '#64748b',
    fontSize: 12,
  },
  subHeaderNote: {
    color: '#94a3b8',
    fontSize: 9,
    fontWeight: '500',
    marginTop: 2,
    display: 'block',
  },
  // --- Card ---
  card: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
  },
  headerCard: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  titleCard: {
    fontWeight: 600,
    fontSize: 15,
    color: '#1e293b',
  },
  // --- Aplikasi Grid ---
  gridCard: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 12,
  },
  menuItemCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: '8px 4px',
  },
  iconWrapperCard: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  labelCard: {
    fontSize: 11,
    color: '#374151',
    textAlign: 'center',
    fontWeight: '500',
    lineHeight: 1.3,
  },
  // --- Carousel ---
  carouselOuter: {
    overflow: 'hidden',
    borderRadius: 10,
    cursor: 'pointer',
    display: 'block',
    textDecoration: 'none',
  },
  carouselTrack: {
    display: 'flex',
  },
  carouselItem: {
    flex: '0 0 100%',
  },
  // --- Berita / Laporan Item ---
  cardBerita: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '10px 12px',
    borderRadius: 10,
    backgroundColor: '#f8fafc',
    marginBottom: 8,
    gap: 10,
  },
  infoBerita: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    minWidth: 0,
  },
  codeBerita: {
    fontWeight: 600,
    fontSize: 13,
    color: '#1e293b',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
  },
  nameBerita: {
    fontSize: 12,
    color: '#64748b',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
  },
  thumbBerita: {
    width: 52,
    height: 52,
    borderRadius: 8,
    objectFit: 'cover',
    flexShrink: 0,
  },
  thumbPlaceholder: {
    width: 52,
    height: 52,
    borderRadius: 8,
    backgroundColor: '#f3f4f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  otherInfoBerita: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 3,
    flexShrink: 0,
  },
  otherInfoText: {
    fontSize: 11,
    color: '#94a3b8',
  },
  // --- View More ---
  viewMoreBtn: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingTop: 12,
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    width: '100%',
  },
  viewMoreText: {
    color: '#007AFF',
    fontSize: 14,
    fontWeight: '500',
  },
};

// ======================== SUB-COMPONENTS ========================

const LaporanItem: React.FC<LaporItemData & { showImage?: boolean }> = ({
  nama, no_pelanggan, status,
}) => {
  const displayNama = nama.length > 7 ? nama.substring(0, 7) + '...' : nama;
  const color = getStatusColor(status);
  return (
    <div style={s.cardBerita}>
      <div style={s.infoBerita}>
        <span style={s.codeBerita}>{displayNama}</span>
        <span style={s.nameBerita}>{no_pelanggan}</span>
        <div style={{
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: color,
          paddingInline: 4,
          paddingBlock: 2,
          borderRadius: 4,
          display: 'inline-block',
          alignSelf: 'flex-start',
          marginTop: 2,
        }}>
          <span style={{ color, fontSize: 12 }}>{status}</span>
        </div>
      </div>
    </div>
  );
};

const BeritaItem: React.FC<BeritaItemData> = ({
  title, slug, author, published_date, read_time_minutes, covert_image_url, views,
}) => {
  const displayTitle = title.length > 25 ? title.substring(0, 25) + '...' : title;
  const displayDate = new Date(published_date).toLocaleDateString('id-ID', {
    day: '2-digit', month: 'short', year: '2-digit',
  });
  // Fix: gunakan URL gambar jika ada, tampilkan placeholder jika tidak
  const imageUrl = covert_image_url?.startsWith('http')
    ? covert_image_url
    : covert_image_url
      ? `https://pdam-dev.alanzulwas.my.id/gt/v1/storage/${covert_image_url}`
      : '';

  return (
    <a
      href={`https://web.tirtamoolango.id/berita/${slug}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: 'none', display: 'block' }}
    >
      <div style={s.cardBerita}>
        {imageUrl ? (
          <img src={imageUrl} alt={displayTitle} style={s.thumbBerita as React.CSSProperties} />
        ) : (
          <div style={s.thumbPlaceholder}>
            <Newspaper size={24} color="#64748b" />
          </div>
        )}
        <div style={s.infoBerita}>
          <span style={s.codeBerita}>{displayTitle}</span>
          <span style={s.nameBerita}>{author}</span>
        </div>
        <div style={s.otherInfoBerita}>
          <span style={s.otherInfoText}>{displayDate}</span>
          <span style={s.otherInfoText}>{views} views • {read_time_minutes} min</span>
        </div>
      </div>
    </a>
  );
};

// ======================== MAIN COMPONENT ========================

export default function HomeMobile() {
  const carouselTrackRef = useRef<HTMLDivElement>(null);
  const carouselIndexRef = useRef(0);

  // ======================== DATA ========================

    const [beritaData, setBeritaData] = useState<BeritaItemData[]>([]);
    const [laporData, setLaporData] = useState<LaporItemData[]>([]);
    const [personalData, setPersonalData] = useState<any>(null);

  const infiniteData = [...laporData]
    .sort((a, b) => new Date(b.modified_at).getTime() - new Date(a.modified_at).getTime())
    .slice(0, 14);

  const dashboardCardItems: DashboardCardItem[] = [
    { label: 'Personalia',        icon: 'person',   bgColor: '#6366f1', route: 'personalia' },
    // { label: 'Laporan Pengaduan', icon: 'document', bgColor: '#ec4899', route: 'lapor' },
  ];

  useEffect(() => {
    fetchBeritaData().then(data => setBeritaData(data.data.filter((item: BeritaItemData) => item.publish === 'publish' && item.published_date).sort((a : any, b : any) => new Date(b.published_date).getTime() - new Date(a.published_date).getTime())));
    fetchLaporData().then(data => setLaporData(data.data));
    fetchPersonalData("001245").then(data => { setPersonalData(data.data[0]); console.log("Personal Data:", data.data[0]); }).catch(err => { console.error("Error fetching personal data:", err); setPersonalData(null); });
  }, []);

  // Auto-scroll carousel (mirrors original FlatList auto-scroll logic)
  useEffect(() => {
    const total = infiniteData.length;
    if (total === 0) return;

    const interval = setInterval(() => {
      const track = carouselTrackRef.current;
      if (!track) return;

      const next = (carouselIndexRef.current + 1) % total;
      carouselIndexRef.current = next;

      track.style.transition = 'transform 0.4s ease';
      track.style.transform = `translateX(-${next * 100}%)`;

      // Reset to start after last item (seamless loop)
      if (next === total - 1) {
        setTimeout(() => {
          const t = carouselTrackRef.current;
          if (t) {
            t.style.transition = 'none';
            t.style.transform = 'translateX(0)';
            carouselIndexRef.current = 0;
          }
        }, 450);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [infiniteData.length]);

  return (
    <div style={s.page}>
      <div style={s.content}>

        {/* ── Header User ── */}
        <div style={{ ...s.headerUser, marginBottom: 0 }}>
          <div style={s.headerLeft}>
            {/* <div style={s.avatar}> */}
            <div>
              {/* <span style={s.avatarText}> */}
              <span className={`w-9 h-9 rounded-full ${s.barBg} border ${s.border} flex items-center justify-center text-[#00d4aa] font-bold text-xs`}>
                {
                  personalData?.foto_karyawan ? 
                    <><img src={process.env.REACT_APP_API_URL + "/storage/route/karyawan/" + encodeURIComponent(personalData.nama) + "/" + personalData.foto_karyawan} alt={personalData.nama} className="w-full h-full object-cover rounded-full" /></>
                      : 
                    <>{(personalData?.nama ?? '').split(' ').map((n : any) => n[0] || '').join('').slice(0, 2)}</>
                }</span>
            </div>
            <div>
              <span style={s.headerTitleUser}>{personalData?.nama || 'User'}</span>
              <span style={s.pointsText}>{personalData?.nik || '1715015052'}</span>
              <span style={s.pointsText}>{personalData?.contract_end_date ? `Kontrak hingga ${new Date(personalData.contract_end_date).toLocaleDateString()}` : 'Kontrak hingga 25 Desember 2026'}</span>
            </div>
          </div>
          <button style={s.iconButton} aria-label="Logout">
            <LogOut size={24} color="white" />
          </button>
        </div>

        {/* ── Sub Header ── */}
        <div style={{ ...s.subHeader, marginTop: 0 }}>
          <span style={s.subHeaderText}>Info Absensi masih dalam pengembangan</span>
          {/* <Calendar size={16} color="#64748b" />
          <div style={{ marginLeft: 8 }}>
            <span style={s.subHeaderText}>Belum ada Keterlambatan</span>
            <span style={s.subHeaderNote}>Diupdate dari tgl 27 Desember 2025</span>
          </div> */}
        </div>

        {/* ── Aplikasi Card ── */}
        <div style={s.card}>
          <div style={s.headerCard}>
            <span style={s.titleCard}>Aplikasi</span>
          </div>
          <div style={s.gridCard}>
            {dashboardCardItems.map((item, idx) => (
              <button
                key={idx + 1}
                style={s.menuItemCard}
                onClick={() => window.location.href = `/#/mobile/${item.route}`}
              >
                <div style={{ ...s.iconWrapperCard, backgroundColor: item.bgColor }}>
                  {getMenuIcon(item.icon, 24, 'white')}
                </div>
                <span style={s.labelCard}>{item.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ── Berita Card ── */}
        <div style={s.card}>
          <div style={s.headerCard}>
            <span style={s.titleCard}>Berita</span>
          </div>
          <div>
            {beritaData
              .filter(item => item.publish === 'publish')
              .sort((a, b) => new Date(b.published_date).getTime() - new Date(a.published_date).getTime())
              .slice(0, 3)
              .map((item, idx) => (
                <BeritaItem key={`v-${idx}`} {...item} />
              ))}
          </div>
          <button
            style={s.viewMoreBtn}
            onClick={() => window.open('https://web.tirtamoolango.id/berita', '_blank')}
          >
            <span style={s.viewMoreText}>View More</span>
            <ChevronDown size={16} color="#007AFF" />
          </button>
        </div>

        {/* ── Laporan Pengaduan Card ── */}
        <div style={s.card}>
          <div style={s.headerCard}>
            <span style={s.titleCard}>Laporan Pengaduan</span>
          </div>
          <a href="/lapor" style={s.carouselOuter}>
            <div ref={carouselTrackRef} style={s.carouselTrack}>
              {infiniteData.map((item, index) => (
                <div key={`v-${index}`} style={s.carouselItem}>
                  <LaporanItem {...item} showImage={false} />
                </div>
              ))}
            </div>
          </a>
        </div>

      </div>
    </div>
  );
}